import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { DateRangeValue } from '@/shared/components/ui/date-range-picker'
import { presetToRange } from '@/shared/constants/filter-options'
import { useAuthStore } from '@/shared/stores/auth.store'

/**
 * Shared filter state for /engine-admin/overview, /engine-admin/costs, and
 * /engine-admin/flag-metrics.
 *
 * Mirrors the client-admin counterpart (and the queue-context snapshot
 * pattern): sessionStorage-persisted so flipping between engine-admin pages
 * — or refreshing any of them — preserves the active scope. Cleared when
 * the tab closes; a fresh browser session lands on defaults.
 *
 * The five core filters (clients / facilities / providers / specialties /
 * date range) live here. Page-specific filters (bucket toggle, module_id,
 * flag_id, report_type_id, analyticsBucket etc.) stay in page-local state
 * since they don't make sense to carry across sibling pages.
 */
const DEFAULT_PRESET = '7d'
function defaultDateRange(): DateRangeValue {
  const { date_from, date_to } = presetToRange(DEFAULT_PRESET)
  return { preset: DEFAULT_PRESET, date_from, date_to }
}

export interface EngineAdminDashboardFilters {
  clientIds: string[]
  facilityIds: string[]
  providerIds: string[]
  specialties: string[]
  dateRange: DateRangeValue
}

function defaultFilters(): EngineAdminDashboardFilters {
  return { clientIds: [], facilityIds: [], providerIds: [], specialties: [], dateRange: defaultDateRange() }
}

interface State extends EngineAdminDashboardFilters {
  /** User id the persisted filters currently belong to. Used to RESET (not
   *  restore per-identity) when a different identity reuses this tab's storage. */
  ownerId: string | null
  setClientIds:   (v: string[]) => void
  setFacilityIds: (v: string[]) => void
  setProviderIds: (v: string[]) => void
  setSpecialties: (v: string[]) => void
  setDateRange:   (v: DateRangeValue) => void
  reset: () => void
  /** Adopt the current identity; reset filters if a different user (or impersonated
   *  identity) reused this tab's storage. Idempotent for the same user. */
  claimOwner: (userId: string) => void
}

export const useEngineAdminFiltersStore = create<State>()(
  persist(
    (set) => ({
      ownerId: null,
      ...defaultFilters(),
      setClientIds:   (v) => set({ clientIds: v }),
      setFacilityIds: (v) => set({ facilityIds: v }),
      setProviderIds: (v) => set({ providerIds: v }),
      setSpecialties: (v) => set({ specialties: v }),
      setDateRange:   (v) => set({ dateRange: v }),
      reset: () => set(defaultFilters()),
      claimOwner: (userId) =>
        set((s) => (s.ownerId === userId ? {} : { ownerId: userId, ...defaultFilters() })),
    }),
    {
      name: 'engine-admin-dashboard-filters',
      storage: createJSONStorage(() => sessionStorage),
      // Bump when the default date preset changes so existing sessions migrate
      // off the stale value instead of rehydrating it.
      version: 3,
      migrate: (persisted: any, _from) => {
        if (persisted && typeof persisted === 'object') {
          persisted.dateRange = defaultDateRange()
        }
        return persisted
      },
      partialize: (s) => ({
        ownerId: s.ownerId,
        clientIds: s.clientIds,
        facilityIds: s.facilityIds,
        providerIds: s.providerIds,
        specialties: s.specialties,
        dateRange: s.dateRange,
      }),
    },
  ),
)

// Reset filters when the active user changes — see client-admin store. A
// different login / impersonated identity reusing this tab doesn't inherit stale
// client/facility selections (ephemeral filters → reset, not per-identity save).
// Covers all consumer pages. Guarded on the full store API being present so this
// module-load side effect no-ops when useAuthStore is mocked as a bare hook in
// unit tests (getState/subscribe absent).
if (typeof useAuthStore.getState === 'function' && typeof useAuthStore.subscribe === 'function') {
  const uid0 = useAuthStore.getState().user?.id
  if (uid0) useEngineAdminFiltersStore.getState().claimOwner(uid0)
  useAuthStore.subscribe((state, prev) => {
    const uid = state.user?.id
    if (uid && uid !== prev.user?.id) useEngineAdminFiltersStore.getState().claimOwner(uid)
  })
}
